import { encryptData, decryptData } from './crypto';

const PREFIX = 'fn_';
const DEFAULT_DAYS = 7;

// Large values (JWTs, permissions JSON) can exceed the ~4 KB browser cookie
// limit and be silently dropped. Store them encrypted in localStorage instead
// (persists until logout, unlike sessionStorage which dies on tab close).
const LS_TOKEN_KEY = 'fn_token';
const LS_PERMS_KEY = 'fn_rolePermissions';

const _lsGetToken  = ()  => { try { return localStorage.getItem(LS_TOKEN_KEY); }  catch { return null; } };
const _lsSetToken  = (v) => { try { localStorage.setItem(LS_TOKEN_KEY, v); }       catch {} };
const _lsRemToken  = ()  => { try { localStorage.removeItem(LS_TOKEN_KEY); }       catch {} };

const _lsGetPerms  = ()  => { try { return localStorage.getItem(LS_PERMS_KEY); }  catch { return null; } };
const _lsSetPerms  = (v) => { try { localStorage.setItem(LS_PERMS_KEY, v); }       catch {} };
const _lsRemPerms  = ()  => { try { localStorage.removeItem(LS_PERMS_KEY); }       catch {} };

const cookieStorage = {
  getItem(key) {
    // Token lives in localStorage (encrypted)
    if (key === 'token') {
      const raw = _lsGetToken();
      if (!raw) return null;
      try { return decryptData(raw) || null; } catch { return null; }
    }

    // Permissions JSON can exceed 4 KB cookie limit — stored in localStorage
    if (key === 'rolePermissions') {
      const raw = _lsGetPerms();
      if (raw) {
        try { return decryptData(raw) || null; } catch { return null; }
      }
      // Migration: move old cookie value to localStorage
      const target = `${PREFIX}${key}=`;
      const pair = document.cookie.split('; ').find(c => c.startsWith(target));
      if (pair) {
        try {
          const decrypted = decryptData(decodeURIComponent(pair.slice(target.length)));
          if (decrypted) {
            _lsSetPerms(encryptData(decrypted));
            document.cookie = `${PREFIX}${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Strict`;
            return decrypted;
          }
        } catch {}
      }
      return null;
    }

    // All other keys: encrypted cookie
    const target = `${PREFIX}${key}=`;
    const pair = document.cookie.split('; ').find(c => c.startsWith(target));
    if (pair) {
      try {
        const decrypted = decryptData(decodeURIComponent(pair.slice(target.length)));
        return decrypted !== '' ? decrypted : null;
      } catch {
        // fall through to migration
      }
    }

    // Migration: move old plain localStorage / sessionStorage value into an encrypted cookie
    try {
      const lsVal = localStorage.getItem(key) ?? sessionStorage.getItem(key);
      if (lsVal !== null) {
        this.setItem(key, lsVal);
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
        return lsVal;
      }
    } catch {}

    return null;
  },

  setItem(key, value, days = DEFAULT_DAYS) {
    const str = String(value === undefined || value === null ? '' : value);

    // Token → encrypted localStorage (no size limit)
    if (key === 'token') {
      _lsSetToken(encryptData(str));
      return;
    }

    // Permissions → encrypted localStorage (avoids 4 KB cookie limit)
    if (key === 'rolePermissions') {
      _lsSetPerms(encryptData(str));
      return;
    }

    // Everything else → encrypted cookie
    const encoded = encodeURIComponent(encryptData(str));
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${PREFIX}${key}=${encoded}; expires=${expires}; path=/; SameSite=Strict${secure}`;
  },

  removeItem(key) {
    if (key === 'token') { _lsRemToken(); return; }
    if (key === 'rolePermissions') { _lsRemPerms(); return; }
    document.cookie = `${PREFIX}${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Strict`;
    try { localStorage.removeItem(key); } catch {}
    try { sessionStorage.removeItem(key); } catch {}
  },

  clear() {
    _lsRemToken();
    _lsRemPerms();
    document.cookie.split('; ').forEach(part => {
      const name = part.split('=')[0];
      if (name.startsWith(PREFIX)) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Strict`;
      }
    });
    try { localStorage.clear(); } catch {}
    try { sessionStorage.clear(); } catch {}
  },
};

export default cookieStorage;
